import React from 'react';
import { Button } from '@/components/ui/button';
import { Clock, Zap, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, isTomorrow, isToday } from 'date-fns';

interface DeliveryTimeIndicatorProps {
  estimatedDelivery: Date;
  variant?: 'inline' | 'prominent' | 'subtle';
  showRushOption?: boolean;
  onRushSelect?: () => void;
  className?: string;
}

// Format delivery time with user-friendly text
const formatDeliveryTime = (date: Date): string => {
  if (isToday(date)) {
    return `Today by ${format(date, 'h:mm a')}`;
  } else if (isTomorrow(date)) {
    return `Tomorrow by ${format(date, 'h:mm a')}`;
  } else {
    return format(date, 'EEEE, MMM d by h:mm a');
  }
};

// Inline variant - minimal, fits within existing UI
const InlineIndicator: React.FC<DeliveryTimeIndicatorProps> = ({
  estimatedDelivery,
  showRushOption,
  onRushSelect,
  className
}) => {
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="flex items-center gap-2 text-sm">
        <Clock className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium">{formatDeliveryTime(estimatedDelivery)}</span>
      </div>
      {showRushOption && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRushSelect}
          className="gap-1 h-7 px-2"
          title="Get it 4 hours earlier for $49"
        >
          <Zap className="w-3 h-3" />
          Rush
        </Button>
      )}
    </div>
  );
};

// Prominent variant - stands out, used in confirmation screens
const ProminentIndicator: React.FC<DeliveryTimeIndicatorProps> = ({
  estimatedDelivery,
  showRushOption,
  onRushSelect,
  className
}) => {
  return (
    <div className={cn("border border-primary/20 bg-primary/5 rounded-lg p-4", className)}>
      <div className="flex items-start gap-3">
        <Truck className="h-5 w-5 text-primary mt-0.5" />
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-base">
                Delivery {formatDeliveryTime(estimatedDelivery)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                We&apos;ll keep you updated on delivery status
              </p>
            </div>
            {showRushOption && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRushSelect}
                className="gap-2"
              >
                <Zap className="w-4 h-4" />
                Rush Delivery
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Subtle variant - simple and compact
const SubtleIndicator: React.FC<DeliveryTimeIndicatorProps> = ({
  estimatedDelivery,
  showRushOption,
  onRushSelect,
  className
}) => {
  return (
    <div className={cn("flex items-center justify-between p-3 border rounded-lg bg-muted/30", className)}>
      <div className="flex items-center gap-2">
        <Clock className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          Delivery {formatDeliveryTime(estimatedDelivery)}
        </span>
      </div>
      {showRushOption && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRushSelect}
          className="gap-1 h-7 px-2"
        >
          <Zap className="w-3 h-3" />
          Rush
        </Button>
      )}
    </div>
  );
};

// Main component with variant selection
export const DeliveryTimeIndicator: React.FC<DeliveryTimeIndicatorProps> = ({
  variant = 'inline',
  ...props
}) => {
  switch (variant) {
    case 'prominent':
      return <ProminentIndicator {...props} />;
    case 'subtle':
      return <SubtleIndicator {...props} />;
    case 'inline':
    default:
      return <InlineIndicator {...props} />;
  }
};

export default DeliveryTimeIndicator;